/**
 * Builds the canvas's tooltip and icon markup without letting user-entered text become markup (#1574).
 *
 * Callout text, tag names and tag presentation values are written by one user and shown to others, so they are
 * escaped before going into a tooltip (which renders HTML) and set as attributes and text, never composed into an
 * HTML string, when drawing icons.
 */

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

const HTML_ESCAPES: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    '\'': '&#39;',
};

/** Escapes text so it shows as itself in HTML. */
export function escapeHtml(text: string): string {
    return text.replace(/[&<>"']/g, (character) => HTML_ESCAPES[character]);
}

/**
 * Breaks text into lines of at most `maxChars` characters, at most `maxLines` of them, as tooltip HTML: each line is
 * escaped and the lines are joined with `<br>`, the only markup in the result.
 */
export function breakTextToTooltipHtml(text: string, maxChars: number, maxLines: number): string {
    const words = text.split(' ');
    let lines: string[] = [];
    let line = '';
    words.forEach((word) => {
        if (line.length + word.length < maxChars) {
            line += word + ' ';
        } else {
            if (line.length > 0)
                lines.push(line);
            if (word.length < maxChars) {
                line = word + ' ';
            } else {
                // slice word into equal parts
                const parts = Math.ceil(word.length / maxChars);
                for (let i = 0; i < parts; i++) {
                    lines.push(word.slice(i * maxChars, (i + 1) * maxChars));
                }
            }
        }
    });
    lines.push(line);
    if (lines.length > maxLines) {
        lines = lines.slice(0, maxLines);
        lines[maxLines - 1] = lines[maxLines - 1].slice(0, maxChars - 3) + '...';
    }
    return lines.map(escapeHtml).join('<br>');
}

/** Creates an SVG element with the given attributes and, optionally, text content. */
export function svgElement(tagName: string, attributes: Record<string, string | number>, text?: string): SVGElement {
    const element = document.createElementNS(SVG_NAMESPACE, tagName);
    Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, String(value)));
    if (text !== undefined) {
        element.textContent = text;
    }
    return element;
}
